import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Croissant, Plus, Search, Tags, Trash2 } from 'lucide-react';
import { computeRecipeCost, formatMoney, toBase, type ProductWithDetail } from '@bakery/shared';

import { api } from '../lib/api.js';
import { byName } from '../lib/catalog-order.js';
import { ProductCategoriesModal } from './ProductCategories.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  NumberInput,
  Modal,
  SectionHeading,
  Select,
  Skeleton,
  cx,
  useToast,
} from '../components/ui.js';

export function CatalogScreen() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ProductWithDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const products = useQuery({
    queryKey: ['products', 'detailed', search],
    queryFn: () => api.catalog.products({ search: search || undefined }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Products &amp; recipes</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
            Bind ingredients to each product and the true cost per loaf follows automatically.
          </p>
        </div>
        <div className="flex gap-2">
          {/* Where you look for them: beside the products they group. */}
          <Button variant="ghost" icon={<Tags className="size-4" />} onClick={() => setCategoriesOpen(true)}>
            Categories
          </Button>
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            New product
          </Button>
        </div>
      </header>

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find a product…" className="pl-9" />
      </div>

      {products.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : products.data?.length === 0 ? (
        <EmptyState icon={<Croissant className="size-8" />} title="No products" description="Add what you sell to fill the register." />
      ) : (
        <ul className="space-y-1.5">
          {products.data?.map((product) => (
            <li key={product.id}>
              <button onClick={() => setEditing(product)} className="card flex w-full items-center gap-4 px-4 py-3 text-left transition hover:border-crust-400">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{product.name}</p>
                  <p className="truncate text-xs text-[var(--color-ink-faint)]">
                    {product.sku}
                    {product.category && ` · ${product.category.name}`}
                    {product.batchYield > 1 && ` · ${product.batchYield} per batch`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular text-sm font-semibold">{formatMoney(product.price)}</p>
                  <p className="tabular text-xs text-[var(--color-ink-faint)]">
                    {typeof product.computedCost === 'number' ? `costs ${formatMoney(product.computedCost)}` : 'no recipe'}
                  </p>
                </div>
                {typeof product.marginPercent === 'number' && (
                  <Badge tone={product.marginPercent > 55 ? 'ok' : product.marginPercent > 35 ? 'warn' : 'danger'}>
                    {product.marginPercent}%
                  </Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && <ProductModal product={editing} onClose={() => setEditing(null)} />}
      {creating && <ProductModal onClose={() => setCreating(false)} />}
      {categoriesOpen && <ProductCategoriesModal onClose={() => setCategoriesOpen(false)} />}
    </div>
  );
}

function ProductModal({ product, onClose }: { product?: ProductWithDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.catalog.categories });
  const ingredients = useQuery({ queryKey: ['ingredients'], queryFn: api.inventory.ingredients });
  const units = useQuery({ queryKey: ['units'], queryFn: api.inventory.units });
  const recipe = useQuery({
    queryKey: ['recipe', product?.id],
    queryFn: () => api.catalog.recipe(product!.id),
    enabled: Boolean(product),
  });

  const [form, setForm] = useState({
    name: product?.name ?? '',
    sku: product?.sku ?? '',
    categoryId: product?.categoryId ?? '',
    price: product?.price ?? 0,
    batchYield: product?.batchYield ?? 1,
    description: product?.description ?? '',
  });

  const [lines, setLines] = useState<{ ingredientId: string; qty: number; unit: string; wastePercent: number }[]>([]);
  const [linesLoaded, setLinesLoaded] = useState(false);

  /*
   * Seed the editor once the saved recipe and the ingredient names are both in
   * hand — the names are what the rows are sorted by, so seeding before they
   * arrive would fix the order wrongly and never revisit it.
   *
   * A recipe is a list to look things up in, not a method: nothing here is in
   * the order it goes in the bowl, and a twenty-line recipe keyed in over two
   * years ends up in the order somebody happened to add things. A–Z is the
   * only order that helps you find the flour.
   */
  if (recipe.data && ingredients.data && !linesLoaded) {
    const nameOf = new Map(ingredients.data.map((ingredient) => [ingredient.id, ingredient.name]));
    const seeded = (recipe.data.lines as { ingredientId: string; qty: number; unit: string; wastePercent: number }[]).map(
      (line) => ({
        ingredientId: line.ingredientId,
        qty: line.qty,
        unit: line.unit,
        wastePercent: line.wastePercent,
      }),
    );
    setLines(
      byName(
        seeded.map((line) => ({ ...line, name: nameOf.get(line.ingredientId) ?? '' })),
      ).map(({ name: _name, ...line }) => line),
    );
    setLinesLoaded(true);
  }

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, categoryId: form.categoryId || null, price: form.price, batchYield: Number(form.batchYield) };
      const saved = product ? await api.catalog.updateProduct(product.id, body) : await api.catalog.createProduct(body);
      if (lines.length > 0 || product) await api.catalog.saveRecipe(saved.id, lines);
      return saved;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['recipe'] });
      toast.push({ tone: 'ok', title: `${saved.name} saved` });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: () => api.catalog.deleteProduct(product!.id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.push({
        tone: 'ok',
        title: result.action === 'archived' ? 'Product archived' : 'Product removed',
        body: result.action === 'archived' ? 'It has been sold before, so its sales history is kept.' : undefined,
      });
      onClose();
    },
  });

  /*
   * Costed here, from what is on screen, rather than read off the saved recipe.
   *
   * It used to show `recipe.data.cost` — the figure the server worked out for
   * whatever was last saved — so adding an ingredient, changing a quantity or
   * changing the batch yield moved nothing until you saved and came back. A new
   * product showed no cost at all, which is the moment you most want to know
   * whether the price covers it.
   *
   * The same function the server commits with, so what is read here and what is
   * stored cannot disagree. Ingredient costs only reach an account allowed to
   * see them, so this is empty for everyone else — as it should be.
   */
  const ingredientsById = useMemo(
    () => new Map((ingredients.data ?? []).map((ingredient) => [ingredient.id, ingredient])),
    [ingredients.data],
  );

  /* The picker is a list to hunt a name in, so A–Z, same as the rows. */
  const pickableIngredients = useMemo(() => byName(ingredients.data ?? []), [ingredients.data]);

  /*
   * Whether this account may see costs at all.
   *
   * The server strips `avgCostPerBase` from ingredients for anyone without
   * `reports.margin`, so costing would quietly treat every ingredient as free
   * and report ₱0.00 a unit with a full margin — a made-up number, and exactly
   * the figure the permission exists to withhold.
   */
  const canCost = (ingredients.data ?? []).some((i) => typeof i.avgCostPerBase === 'number');

  const cost = useMemo(() => {
    if (lines.length === 0 || !canCost) return null;
    return computeRecipeCost({
      lines: lines.map((line, index) => ({
        id: String(index),
        productId: product?.id ?? '',
        ingredientId: line.ingredientId,
        qty: line.qty,
        unit: line.unit,
        // The editor holds a quantity and a unit; costing works in base units.
        baseQty: toBase(line.qty, line.unit),
        wastePercent: line.wastePercent,
        notes: null,
        sortOrder: index,
      })),
      ingredientsById,
      batchYield: Number(form.batchYield) || 1,
    });
  }, [lines, ingredientsById, form.batchYield, product?.id, canCost]);

  const margin = cost && form.price > 0 ? Math.round(((form.price - cost.costPerUnit) / form.price) * 1000) / 10 : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={product ? product.name : 'New product'}
      footer={
        <>
          {product && (
            <Button variant="ghost" icon={<Trash2 className="size-4" />} loading={remove.isPending} onClick={() => remove.mutate()}>
              Delete
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.name} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" required>
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoFocus />
          </Field>
          <Field label="Category">
            <Select value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })}>
              <option value="">None</option>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Selling price">
            <NumberInput
              value={form.price / 100}
              onChange={(pesos) => setForm({ ...form, price: Math.round(pesos * 100) })}
              className="tabular text-right"
            />
          </Field>
          <Field label="Units per batch" hint="The recipe below makes this many.">
            <NumberInput
              inputMode="numeric"
              value={form.batchYield}
              onChange={(batchYield) => setForm({ ...form, batchYield })}
              className="tabular text-right"
            />
          </Field>
        </div>

        {/* Recipe ------------------------------------------------------- */}
        <section>
          <SectionHeading
            title="Recipe"
            hint="Quantities are per batch. Waste covers what stays in the mixer."
            action={
              cost && (
                <div className="text-right text-sm">
                  <p className="tabular font-semibold">{formatMoney(cost.costPerUnit)} / unit</p>
                  {margin !== null && <p className="text-xs text-[var(--color-ink-faint)]">{margin}% margin</p>}
                </div>
              )
            }
          />

          {cost && !cost.isComplete && (
            <p className="mb-2 rounded-[var(--radius-md)] bg-[var(--color-warn-soft)] px-3 py-2 text-xs text-[var(--color-warn)]">
              Some ingredients have never been purchased, so this cost is an underestimate. Record a delivery to price them.
            </p>
          )}

          <div className="space-y-2">
            {lines.map((line, index) => {
              const ingredient = ingredients.data?.find((i) => i.id === line.ingredientId);
              const compatible = units.data?.byDimension[ingredient?.dimension ?? 'mass'] ?? [];

              return (
                <div key={index} className="grid grid-cols-12 items-center gap-2">
                  <div className="col-span-5">
                    <Select
                      value={line.ingredientId}
                      onChange={(event) => {
                        const next = ingredients.data?.find((i) => i.id === event.target.value);
                        setLines((current) =>
                          current.map((l, i) =>
                            i === index ? { ...l, ingredientId: event.target.value, unit: next?.stockUnit ?? l.unit } : l,
                          ),
                        );
                      }}
                    >
                      {pickableIngredients.map((option) => (
                        <option key={option.id} value={option.id}>{option.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <NumberInput
                      value={line.qty}
                      onChange={(qty) => setLines((c) => c.map((l, i) => (i === index ? { ...l, qty } : l)))}
                      className="tabular text-right"
                    />
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={line.unit}
                      onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, unit: event.target.value } : l)))}
                    >
                      {compatible.map((unit) => (
                        <option key={unit.code} value={unit.code}>{unit.code}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <NumberInput
                      value={line.wastePercent}
                      onChange={(wastePercent) => setLines((c) => c.map((l, i) => (i === index ? { ...l, wastePercent } : l)))}
                      className="tabular text-right"
                      title="Waste %"
                    />
                  </div>
                  <button className="col-span-1" onClick={() => setLines((c) => c.filter((_, i) => i !== index))} aria-label="Remove ingredient">
                    <Trash2 className="size-4 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]" />
                  </button>
                </div>
              );
            })}

            <Button
              size="sm"
              icon={<Plus className="size-4" />}
              disabled={!ingredients.data?.length}
              onClick={() =>
                setLines((current) => [
                  ...current,
                  { ingredientId: ingredients.data![0]!.id, qty: 1, unit: ingredients.data![0]!.stockUnit, wastePercent: 0 },
                ])
              }
            >
              Add an ingredient
            </Button>
          </div>
        </section>
      </div>
    </Modal>
  );
}
